// Product catalog. All five products are live with real deliverables
// (see backend/static/products/). Swap shopUrl with Creem checkout
// links per product when payment goes live.
export const CATEGORIES = ["All", "AI Kits", "Templates", "Workflows"];

export const products = [
  // ---------- 001 — LIVE ----------
  {
    slug: "ai-writing-kit",
    no: "001",
    name: "AI Writing Kit",
    category: "AI Kits",
    status: "available",
    price: "$19",
    shopUrl: "https://vaymilo.com",
    tagline:
      "Prompts, workflows, and ready-to-use templates for everyday writing.",
    description:
      "A simple starter kit for people who want to write better emails, captions, notes, summaries, ideas, and everyday content using AI. No fluff, no hype — just a calm system you can use today.",
    audience: "Students, creators, freelancers, modern teams",
    access: "Instant download",
    zipName: "vaymilo-ai-writing-kit.zip",
    files: [
      { name: "00-START-HERE.md", desc: "The map: what you own, the 15-minute start, the week-one path" },
      { name: "01-the-kit.md", desc: "The core — 5 calm rules, the prompt formula, daily & weekly workflows, all 100 prompts" },
      { name: "02-copy-ready-templates.md", desc: "20 fill-in-the-blank emails & captions that need no AI at all" },
      { name: "03-voice-and-style-worksheet.md", desc: "Build your personal style guide + the voice calibration block" },
      { name: "04-advanced-prompting.md", desc: "Prompt chaining, iteration loops, output rescue, how the models differ" },
      { name: "05-cheat-sheet.md", desc: "The whole kit on one printable page" },
    ],
    features: [
      "100 hand-tested writing prompts",
      "20 copy-ready templates — no AI needed",
      "Voice & style worksheet + calibration block",
      "Advanced prompting: chains, loops, output rescue",
      "Daily & weekly writing workflows",
      "One-page cheat sheet + every kit update",
    ],
    inside: [
      { label: "Email prompts", note: "Cold, follow-up, polite no, intros" },
      { label: "Caption prompts", note: "Hooks, CTAs, story openers" },
      { label: "Summary prompts", note: "Articles, meetings, books" },
      { label: "Idea prompts", note: "Newsletters, threads, products" },
      { label: "Daily writing workflows", note: "30-min calm routines" },
    ],
    faq: [
      {
        q: "Do I need ChatGPT or Claude?",
        a: "Any modern AI chat tool works (ChatGPT, Claude, Gemini, Copilot). The kit is model-agnostic.",
      },
      {
        q: "What format are the files in?",
        a: "Clean, copy-ready text (Notion-friendly + Markdown). Open them anywhere.",
      },
      {
        q: "Is this only for writers?",
        a: "No — most users are freelancers, students, and small-team operators who write as part of their day.",
      },
    ],
  },

  // ---------- 002 — Notion Planner Kit ----------
  {
    slug: "notion-planner-kit",
    no: "002",
    name: "Notion Planner Kit",
    category: "Templates",
    status: "available",
    price: "$15",
    shopUrl: "https://vaymilo.com",
    tagline:
      "A calm planner system for weekly goals, daily reviews, and habits.",
    description:
      "Drop-in Notion templates for people who want a single place to plan the week, run a clean daily review, and quietly track habits — without the productivity-influencer aesthetic. Built to be opened, used in five minutes, and closed.",
    audience: "Solo operators, students, freelancers, makers",
    access: "Instant download",
    zipName: "vaymilo-notion-planner-kit.zip",
    files: [
      { name: "00-START-HERE.md", desc: "Setup two ways: the 15-min import or the 5-min AI build" },
      { name: "01-weekly-planner.md", desc: "Goals, top 3 by day, blockers, the Friday review" },
      { name: "02-daily-review.md", desc: "The 3-question daily review — 2 min morning, 3 min evening" },
      { name: "03-habit-tracker.md", desc: "Monthly checkboxes, deliberately streak-free" },
      { name: "04-project-tracker.md", desc: "Status · Owner · Next action — projects that don't die" },
      { name: "05-reading-list.md", desc: "Books & articles + the notes companion for finished reads" },
      { name: "06-ai-build-prompt.md", desc: "One prompt that builds the whole system in your Notion" },
      { name: "07-quarterly-planning.md", desc: "3 quarter goals, the not-doing list, monthly checkpoints" },
      { name: "08-annual-review.md", desc: "The 90-minute year review — evidence in, verdict out" },
    ],
    features: [
      "Weekly planner + 3-question daily review",
      "Habit tracker with monthly reset — no streak guilt",
      "Project tracker: status, owner, next action",
      "Quarterly planning + annual review templates",
      "Reading list + notes companion",
      "Setup guide (15 mins) + AI build prompt",
    ],
    inside: [
      {
        label: "Weekly view",
        note: "Goals, priorities, top 3, blockers",
      },
      {
        label: "Daily view",
        note: "Today, tomorrow, journal, gratitude",
      },
      {
        label: "Habit tracker",
        note: "Simple checkboxes, no streak guilt",
      },
      {
        label: "Project tracker",
        note: "Status · Owner · Next action",
      },
      {
        label: "Reading list",
        note: "Books, articles, key notes",
      },
    ],
    faq: [
      {
        q: "Does this work on the free Notion plan?",
        a: "Yes — every template is designed for the free Notion tier. No paid plan required.",
      },
      {
        q: "Can I duplicate it to a team workspace?",
        a: "Absolutely. The licence is single-user, but you can move it into a team workspace for personal use.",
      },
      {
        q: "What if Notion isn't my tool?",
        a: "Right now, this kit is Notion-only. We're considering Apple Notes and Obsidian versions in 2026.",
      },
    ],
  },

  // ---------- 003 — Content Workflow System ----------
  {
    slug: "content-workflow-system",
    no: "003",
    name: "Content Workflow System",
    category: "Workflows",
    status: "available",
    price: "$29",
    shopUrl: "https://vaymilo.com",
    tagline: "Idea → draft → publish, in one repeatable flow.",
    description:
      "A step-by-step system for moving content from rough idea to published piece. Built for creators who want a real process instead of staring at a blank document. Works for blog posts, threads, newsletters, scripts, and case studies.",
    audience:
      "Creators, freelance writers, indie founders, content teams of 1–5",
    access: "Instant download",
    zipName: "vaymilo-content-workflow-system.zip",
    files: [
      { name: "00-START-HERE.md", desc: "The map + the 45-minute setup" },
      { name: "01-the-system.md", desc: "The 5 stages, outlines for 6 content types, the 12-item checklist, the weekly cadence" },
      { name: "02-worked-example.md", desc: "One real idea through all five stages — messy artifacts included" },
      { name: "03-templates-pack.md", desc: "7 copy-paste templates: idea bank, shaping worksheet, calendar, distribution log…" },
      { name: "04-30-day-launch-plan.md", desc: "A day-by-day first month for starting (or restarting) from zero" },
      { name: "05-cheat-sheet.md", desc: "The whole system on one printable page" },
    ],
    features: [
      "5-stage workflow: capture → shape → draft → polish → publish",
      "Full worked example — one idea through all 5 stages",
      "Outline frameworks for 6 content types",
      "7-template pack: idea bank, calendar, distribution log",
      "30-day launch plan for starting from zero",
      "12-item self-edit checklist + one-page cheat sheet",
    ],
    inside: [
      {
        label: "Capture",
        note: "Idea bank · raw notes · voice memos",
      },
      {
        label: "Shape",
        note: "Pick angle · audience · 1-sentence promise",
      },
      {
        label: "Draft",
        note: "Outline → ugly draft → second draft",
      },
      {
        label: "Polish",
        note: "Edit pass · clarity check · tighten",
      },
      {
        label: "Publish",
        note: "SEO basics · distribution · repurpose",
      },
    ],
    faq: [
      {
        q: "Is this AI-assisted or fully manual?",
        a: "Both — the workflow is the system; AI tools are optional accelerators at each stage. Works either way.",
      },
      {
        q: "How long does one piece take?",
        a: "Most users finish a 1,200-word post in 90 minutes after a few rounds with the system. Faster as the idea bank grows.",
      },
      {
        q: "Can a small team share this?",
        a: "The licence is single-user. For team adoption, get in touch — we're working on a small-team licence.",
      },
    ],
  },

  // ---------- 004 — Summary Prompts Pack ----------
  {
    slug: "summary-prompts-pack",
    no: "004",
    name: "Summary Prompts Pack",
    category: "AI Kits",
    status: "available",
    price: "$9",
    shopUrl: "https://vaymilo.com",
    tagline:
      "50 prompts to turn long articles, meetings, and notes into clean summaries.",
    description:
      "A focused prompt pack with one job: turning long content into clear, structured summaries. Built for school, work, research, and weekly recaps. Works with any modern AI chat tool — no setup, no extension, no learning curve.",
    audience: "Students, researchers, knowledge workers, weekly readers",
    access: "Instant download",
    zipName: "vaymilo-summary-prompts-pack.zip",
    files: [
      { name: "00-START-HERE.md", desc: "The map + the 10-minute start" },
      { name: "01-the-pack.md", desc: "All 50 prompts + the 4 length and 4 tone presets" },
      { name: "02-advanced-combos.md", desc: "12 recipes: multi-doc digests, summary-of-summaries, recurring weekly systems" },
      { name: "03-meeting-notes-os.md", desc: "The complete before / during / after meeting system" },
      { name: "04-research-triage.md", desc: "The triage funnel, the claims ledger, the reading queue that doesn't rot" },
      { name: "05-quick-reference-card.md", desc: "The whole pack on one printable page" },
    ],
    features: [
      "50 hand-tested summary prompts",
      "Length & tone presets — 8, freely combinable",
      "12 advanced combo recipes (multi-doc, digests)",
      "Complete meeting-notes operating system",
      "Research triage workflow + claims ledger",
      "Printable quick reference card",
    ],
    inside: [
      {
        label: "Article summaries",
        note: "Long-reads, papers, posts, threads",
      },
      {
        label: "Meeting summaries",
        note: "Actions · decisions · open questions",
      },
      {
        label: "Book summaries",
        note: "Chapter-level + 1-page synthesis",
      },
      {
        label: "Research summaries",
        note: "Claims, evidence, gaps, takeaways",
      },
      {
        label: "Personal recaps",
        note: "Weekly review, journal, study log",
      },
    ],
    faq: [
      {
        q: "Do I need to be good at prompting?",
        a: "No — copy a prompt, paste your text, change the length preset if needed. That's it.",
      },
      {
        q: "Will it work for non-English content?",
        a: "Yes for the major languages most AI models support. Quality is best in English, Hindi, Spanish, and French in our testing.",
      },
      {
        q: "Is this redundant with the AI Writing Kit?",
        a: "The Writing Kit covers writing broadly; this pack is laser-focused on summaries. Some buyers get both — most pick one based on the work they do most.",
      },
    ],
  },

  // ---------- 005 — Vaymilo Starter Bundle ----------
  {
    slug: "vaymilo-starter-bundle",
    no: "005",
    name: "Vaymilo Starter Bundle",
    category: "AI Kits",
    status: "available",
    price: "$35",
    originalPrice: "$43",
    shopUrl: "https://vaymilo.com",
    tagline:
      "AI Writing Kit + Notion Planner Kit + Summary Prompts Pack — at one calmer price.",
    description:
      "Three of our best kits, bundled. Designed to work together: write better, plan calmer weeks, and turn long content into clean summaries — instant downloads, or all included with a Studio membership.",
    audience: "New customers picking their first Vaymilo kit",
    access: "Instant download",
    zipName: "vaymilo-starter-bundle.zip",
    files: [
      { name: "00-STARTER-MAP.md", desc: "Bundle bonus: where to begin, by use case — student, creator, freelancer, small team" },
      { name: "ai-writing-kit/", desc: "The complete AI Writing Kit — all 6 files" },
      { name: "summary-prompts-pack/", desc: "The complete Summary Prompts Pack — all 6 files" },
      { name: "notion-planner-kit/", desc: "The complete Notion Planner Kit — all 9 files" },
    ],
    features: [
      "Includes AI Writing Kit (worth $19)",
      "Includes Notion Planner Kit (worth $15)",
      "Includes Summary Prompts Pack (worth $9)",
      "Save $8 vs. buying separately",
      "All updates to each kit, forever",
      "Or get everything with a Studio membership",
    ],
    inside: [
      {
        label: "AI Writing Kit",
        note: "Prompts, workflows, copy-ready templates",
      },
      {
        label: "Notion Planner Kit",
        note: "Weekly · Daily · Habits · Projects",
      },
      {
        label: "Summary Prompts Pack",
        note: "50 prompts · length & tone presets",
      },
      {
        label: "Bonus: Starter map",
        note: "Where to begin, by use case",
      },
    ],
    faq: [
      {
        q: "Can I buy them separately later?",
        a: "Yes — each kit is also sold standalone. The bundle just saves you $8 at the start.",
      },
      {
        q: "Are the bundled kits the same as the individual ones?",
        a: "Exactly the same files. No stripped-down 'bundle editions'.",
      },
      {
        q: "What if I already bought one of the kits?",
        a: "Email us at hello@vaymilo.com — we'll work something out so you only pay for the new ones.",
      },
    ],
  },
];

export const getProduct = (slug) => products.find((p) => p.slug === slug);
